import { makeAutoObservable, runInAction } from "mobx";

import type {
  ArchiveRecordsUseCase,
  AssignRecordToProjectUseCase,
  ChangeRecordStatusUseCase,
  DeleteRecordsUseCase,
  FetchAdminRecordsPaginatedUseCase,
  FetchRecordDetailsUseCase,
  FetchRecordMarkdownUseCase,
  FetchRecordMetaUseCase,
  FetchRecordsListUseCase,
  FetchRecordsMetaBatchUseCase,
  FetchRecordsPaginatedUseCase,
  RenameRecordUseCase,
  RemoveFromProjectUseCase,
} from "@/application/records/use-cases";
import type { ToastServicePort } from "@/application/records/ports/ToastServicePort";
import type { MarkdownEndpoint } from "@/application/records/ports/RecordsGatewayPort";
import type { DetailedReportResponse } from "@/lib/types";
import type {
  PaginatedRecordsResponseDTO,
  RecordListItemDTO,
  RecordsFiltersDTO,
} from "@/application/records/dtos/RecordListItemDTO";
import type { RecordsMetaBatchRequestOptions } from "@/lib/records-meta-batch";

import {
  RecordsListStore,
  type RecordsListQueryResult,
} from "./RecordsListStore";
import { RecordDetailsStore } from "./RecordDetailsStore";
import { RecordsMetaStore } from "./RecordsMetaStore";
import { RecordsMutationsStore } from "./RecordsMutationsStore";
import { RecordsStateManager } from "./RecordsStateManager";

export interface RecordsRootStoreDependencies {
  fetchRecordsListUseCase: FetchRecordsListUseCase;
  fetchRecordsPaginatedUseCase: FetchRecordsPaginatedUseCase;
  fetchAdminRecordsPaginatedUseCase: FetchAdminRecordsPaginatedUseCase;
  fetchRecordDetailsUseCase: FetchRecordDetailsUseCase;
  fetchRecordMarkdownUseCase: FetchRecordMarkdownUseCase;
  fetchRecordMetaUseCase: FetchRecordMetaUseCase;
  fetchRecordsMetaBatchUseCase: FetchRecordsMetaBatchUseCase;
  deleteRecordsUseCase: DeleteRecordsUseCase;
  archiveRecordsUseCase: ArchiveRecordsUseCase;
  renameRecordUseCase: RenameRecordUseCase;
  removeFromProjectUseCase: RemoveFromProjectUseCase;
  assignRecordToProjectUseCase: AssignRecordToProjectUseCase;
  changeRecordStatusUseCase: ChangeRecordStatusUseCase;
  toastService: ToastServicePort;
}

export class RecordsRootStore {
  readonly userListStore: RecordsListStore;
  readonly adminListStore: RecordsListStore;
  readonly metaStore: RecordsMetaStore;
  readonly mutationsStore: RecordsMutationsStore;
  readonly stateManager: RecordsStateManager;

  private refreshToken = 0;
  private readonly detailsStoreOrder: string[] = [];
  private readonly maxDetailStores = 10;

  private readonly fetchRecordsListUseCase: FetchRecordsListUseCase;
  private readonly fetchRecordDetailsUseCase: FetchRecordDetailsUseCase;
  private readonly fetchRecordMarkdownUseCase: FetchRecordMarkdownUseCase;

  private readonly detailsStores = new Map<string, RecordDetailsStore>();

  constructor(deps: RecordsRootStoreDependencies) {
    this.fetchRecordsListUseCase = deps.fetchRecordsListUseCase;
    this.fetchRecordDetailsUseCase = deps.fetchRecordDetailsUseCase;
    this.fetchRecordMarkdownUseCase = deps.fetchRecordMarkdownUseCase;

    this.stateManager = new RecordsStateManager();

    this.userListStore = new RecordsListStore({
      mode: "user",
      fetchUserPaginatedUseCase: deps.fetchRecordsPaginatedUseCase,
      fetchAdminPaginatedUseCase: deps.fetchAdminRecordsPaginatedUseCase,
      stateManager: this.stateManager,
    });

    this.adminListStore = new RecordsListStore({
      mode: "admin",
      fetchUserPaginatedUseCase: deps.fetchRecordsPaginatedUseCase,
      fetchAdminPaginatedUseCase: deps.fetchAdminRecordsPaginatedUseCase,
      stateManager: this.stateManager,
    });

    this.metaStore = new RecordsMetaStore({
      fetchRecordMetaUseCase: deps.fetchRecordMetaUseCase,
      fetchRecordsMetaBatchUseCase: deps.fetchRecordsMetaBatchUseCase,
    });

    this.mutationsStore = new RecordsMutationsStore({
      deleteRecordsUseCase: deps.deleteRecordsUseCase,
      archiveRecordsUseCase: deps.archiveRecordsUseCase,
      renameRecordUseCase: deps.renameRecordUseCase,
      removeFromProjectUseCase: deps.removeFromProjectUseCase,
      assignRecordToProjectUseCase: deps.assignRecordToProjectUseCase,
      changeRecordStatusUseCase: deps.changeRecordStatusUseCase,
      toastService: deps.toastService,
      userListStore: this.userListStore,
      adminListStore: this.adminListStore,
      metaStore: this.metaStore,
      stateManager: this.stateManager,
    });

    makeAutoObservable(this, {}, { autoBind: true });
  }

  get refreshVersion(): number {
    return this.refreshToken;
  }

  async ensureUserRecords(
    limit: number,
    enabled: boolean,
    filters?: RecordsFiltersDTO
  ): Promise<void> {
    await this.userListStore.ensureInitialLoad(limit, enabled, filters);
  }

  async ensureAdminRecords(
    limit: number,
    enabled: boolean,
    filters?: RecordsFiltersDTO
  ): Promise<void> {
    await this.adminListStore.ensureInitialLoad(limit, enabled, filters);
  }

  async setUserFilters(
    limit: number,
    filters: RecordsFiltersDTO,
    enabled: boolean
  ): Promise<void> {
    await this.userListStore.setFilters(limit, filters, { enabled });
  }

  async setAdminFilters(
    limit: number,
    filters: RecordsFiltersDTO,
    enabled: boolean
  ): Promise<void> {
    await this.adminListStore.setFilters(limit, filters, { enabled });
  }

  getUserRecordsQuery(limit: number): RecordsListQueryResult {
    return this.userListStore.getQueryResult(limit);
  }

  getAdminRecordsQuery(limit: number): RecordsListQueryResult {
    return this.adminListStore.getQueryResult(limit);
  }

  async refetchUserRecords(limit: number): Promise<void> {
    await this.userListStore.refetch(limit);
  }

  async refetchAdminRecords(limit: number): Promise<void> {
    await this.adminListStore.refetch(limit);
  }

  async fetchFullRecordsList(limit?: number) {
    return this.fetchRecordsListUseCase.execute({ limit });
  }

  initializeRecords(records: RecordListItemDTO[]): void {
    this.stateManager.initializeRecords(records);
  }

  getRecordsState(): ReadonlyMap<string, RecordListItemDTO> {
    return this.stateManager.getAllRecords();
  }

  getStateStats() {
    return this.stateManager.getStats();
  }

  private ensureDetailsStore(recordId: string): RecordDetailsStore {
    let store = this.detailsStores.get(recordId);
    const isNewStore = !store;

    if (!store) {
      store = new RecordDetailsStore({
        recordId,
        fetchDetailsUseCase: this.fetchRecordDetailsUseCase,
        fetchMarkdownUseCase: this.fetchRecordMarkdownUseCase,
      });
    }

    const storeInstance = store!;

    runInAction(() => {
      if (isNewStore) {
        this.detailsStores.set(recordId, storeInstance);
      }

      const lastIndex = this.detailsStoreOrder.length - 1;
      if (isNewStore || this.detailsStoreOrder[lastIndex] !== recordId) {
        const existingIndex = this.detailsStoreOrder.indexOf(recordId);
        if (existingIndex >= 0) {
          this.detailsStoreOrder.splice(existingIndex, 1);
        }
        this.detailsStoreOrder.push(recordId);
      }

      while (this.detailsStoreOrder.length > this.maxDetailStores) {
        const evictedId = this.detailsStoreOrder.shift();
        if (!evictedId) {
          break;
        }
        const evictedStore = this.detailsStores.get(evictedId);
        evictedStore?.reset();
        this.detailsStores.delete(evictedId);
      }
    });

    return storeInstance;
  }

  async loadRecordDetails(
    recordId: string,
    version?: number | null,
    options?: { force?: boolean; background?: boolean }
  ): Promise<void> {
    const detailsStore = this.ensureDetailsStore(recordId);
    await detailsStore.loadDetails(version ?? null, options);
  }

  async loadRecordMarkdown(
    recordId: string,
    reportType: MarkdownEndpoint,
    version?: number | null,
    options?: { force?: boolean }
  ): Promise<void> {
    const detailsStore = this.ensureDetailsStore(recordId);
    await detailsStore.loadMarkdown(reportType, version ?? null, options);
  }

  getRecordDetails(
    recordId: string,
    version?: number | null
  ): DetailedReportResponse | null {
    const store = this.detailsStores.get(recordId);
    if (!store) {
      return null;
    }
    return store.getDetailsEntry(version).data;
  }

  getRecordDetailsEntry(recordId: string, version?: number | null) {
    const store =
      this.detailsStores.get(recordId) ?? this.ensureDetailsStore(recordId);
    return store.getDetailsEntry(version);
  }

  getRecordMarkdownEntry(
    recordId: string,
    reportType: MarkdownEndpoint,
    version?: number | null
  ) {
    const store =
      this.detailsStores.get(recordId) ?? this.ensureDetailsStore(recordId);
    return store.getMarkdownEntry(reportType, version);
  }

  async loadRecordMeta(recordId: string, include?: string[]): Promise<void> {
    await this.metaStore.loadRecordMeta(recordId, include);
  }

  async loadRecordsMetaBatch(
    recordIds: string[],
    options?: RecordsMetaBatchRequestOptions
  ): Promise<void> {
    await this.metaStore.loadRecordsMetaBatch(recordIds, options);
  }

  getRecordMetaEntry(recordId: string, include?: string[]) {
    return this.metaStore.getRecordMetaEntry(recordId, include);
  }

  getRecordsMetaBatchEntry(recordIds: string[], include?: string[]) {
    return this.metaStore.getRecordsMetaBatchEntry(recordIds, include);
  }

  invalidateRecords(): void {
    runInAction(() => {
      this.userListStore.invalidateAll();
      this.adminListStore.invalidateAll();
      this.metaStore.clear();
      this.refreshToken += 1;
    });
  }

  invalidateRecordDetails(recordId: string): void {
    runInAction(() => {
      const store = this.detailsStores.get(recordId);
      if (store) {
        store.reset();
        this.refreshToken += 1;
      }
    });
  }

  async refreshRecordDetails(recordId: string): Promise<void> {
    await this.loadRecordDetails(recordId, null, {
      force: true,
      background: true,
    });
  }

  async refreshRecordMeta(recordId: string): Promise<void> {
    await this.metaStore.refreshRecord(recordId);
  }

  clearCaches(): void {
    runInAction(() => {
      this.stateManager.reset();
      this.userListStore.resetAllStates();
      this.adminListStore.resetAllStates();
      this.metaStore.clear();
      this.refreshToken += 1;
      this.detailsStores.forEach((store) => store.reset());
      this.detailsStores.clear();
      this.detailsStoreOrder.length = 0;
    });
  }

  getUserRecordsSnapshot(): Map<number, PaginatedRecordsResponseDTO[]> {
    return this.userListStore.createSnapshot();
  }

  getAdminRecordsSnapshot(): Map<number, PaginatedRecordsResponseDTO[]> {
    return this.adminListStore.createSnapshot();
  }
}
